// /devices — account-level list of devices signed in to the account (the app's
// password sign-in, and the device-authorization flow the Apple TV uses), with a
// revoke action, and the pairing keys that sign development devices in (TOM-471,
// PairingKeys.tsx), and the access tokens for AI apps (TOM-841, McpTokens.tsx).
// A device with no label shows as "Unnamed device" (TOM-559).
// Auth-gated via AppShell's RequireAuth. Distinct from the boat-scoped hardware
// inventory (/dashboard/boats/:boatId/devices, pages/Devices.tsx).

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { McpTokens } from './McpTokens';
import { PairingKeys } from './PairingKeys';

interface LinkedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  /** The pairing key it signed in with (TOM-471), or null. */
  pairingKey: { id: string; label: string } | null;
}

interface ApiError {
  body?: { message?: string };
  message?: string;
}

function formatWhen(iso: string | null): string {
  if (iso === null) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function LinkedDevices() {
  useDocumentTitle('Linked devices - Neomaris');
  const [devices, setDevices] = useState<LinkedDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<{ devices: LinkedDevice[] }>('/v1/device/list');
      setDevices(res.devices);
    } catch (err) {
      const e = err as ApiError;
      setError(e.body?.message ?? e.message ?? 'Couldn’t load your devices.');
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(id: string) {
    setRevokingId(id);
    setError(null);
    try {
      await api(`/v1/device/${id}/revoke`, { method: 'POST' });
      await load();
    } catch (err) {
      const e = err as ApiError;
      setError(e.body?.message ?? e.message ?? 'Couldn’t revoke that device.');
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <section className="dashboard devices-shell">
      <header className="dashboard-head">
        <h2>Linked devices</h2>
        <p className="dashboard-sub">
          Phones, simulators, Apple TVs and other devices signed in to your account. Revoking a
          device signs it out immediately.
        </p>
      </header>

      {error !== null && (
        <ul className="form-errors" data-testid="error-banner">
          <li>{error}</li>
        </ul>
      )}

      {devices === null ? (
        <p className="dashboard-muted">Loading…</p>
      ) : devices.length === 0 ? (
        <div className="tb-card devices-empty">
          <p className="dashboard-muted" data-testid="devices-empty">
            No devices are signed in yet. Sign in to the Neomaris app on your iPhone or iPad, or
            approve the code an Apple TV shows.
          </p>
        </div>
      ) : (
        <ul className="tb-list devices-list">
          {devices.map((d) => (
            <li key={d.id} className="tb-list__row" data-testid="linked-device">
              <span className="tb-list__body">
                <span className="devices-list__text">
                  <strong className="devices-list__name">{d.label ?? 'Unnamed device'}</strong>
                  <span className="devices-list__meta">
                    Signed in {formatWhen(d.createdAt)} · last active {formatWhen(d.lastSeenAt)}
                    {d.pairingKey !== null && <> · key “{d.pairingKey.label}”</>}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn devices-revoke"
                  onClick={() => void revoke(d.id)}
                  disabled={revokingId === d.id}
                >
                  {revokingId === d.id ? 'Revoking…' : 'Revoke'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <PairingKeys onDevicesChanged={() => void load()} />
      <McpTokens />
    </section>
  );
}
